import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { InlineLoginForm } from './inline-login-form';
import { EmailVerificationPrompt } from './email-verification-prompt';
import { parseAppCode, getAppCodeFromURL } from '@/lib/utils/url';
import { getSaaSPrettyName } from '@/config/constants';
import { handoffLoginUrl } from '@/lib/account-handoff';

/** True when the app is running inside an iframe (CRM embed). */
const isInIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

interface AuthGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

/** Skeleton that mirrors the AppLayout shell (header + sidebar + content) */
function AppSkeleton() {
  return (
    <div className="flex min-h-screen flex-col bg-background animate-pulse">
      {/* Header skeleton */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 shadow-sm">
        <div className="flex h-14 items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-6">
            <div className="h-5 w-20 rounded bg-muted" />
            <div className="hidden md:flex items-center gap-2">
              <div className="h-8 w-24 rounded-lg bg-muted" />
              <div className="h-8 w-20 rounded-lg bg-muted" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-7 w-16 rounded-full bg-muted" />
            <div className="h-7 w-7 rounded-full bg-muted" />
            <div className="h-7 w-7 rounded-full bg-muted" />
            <div className="ml-2 pl-2 border-l border-border">
              <div className="h-7 w-7 rounded-full bg-muted" />
            </div>
          </div>
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar skeleton */}
        <aside className="hidden lg:flex w-56 flex-col border-r border-border bg-card/50">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded bg-muted" />
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-muted" />
                <div className="h-2.5 w-12 rounded bg-muted" />
              </div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            <div className="h-9 w-full rounded-lg bg-muted" />
            <div className="h-9 w-full rounded-lg bg-muted" />
            <div className="h-9 w-full rounded-lg bg-muted" />
          </nav>
        </aside>

        {/* Content skeleton */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-4 w-72 rounded bg-muted" />
            <div className="mt-6 space-y-3">
              <div className="h-10 w-full rounded-lg bg-muted" />
              <div className="h-32 w-full rounded-lg bg-muted" />
              <div className="h-10 w-full rounded-lg bg-muted" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export function AuthGuard({ children, requireAdmin = false }: AuthGuardProps) {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const initialized = useAuthStore((s) => s.initialized);
  const needsEmailVerification = useAuthStore((s) => s.needsEmailVerification);
  const storeSuggestedEmail = useAuthStore((s) => s.suggestedLoginEmail);
  const location = useLocation();

  // Initialize the Firebase auth listener on mount
  useEffect(() => {
    const unsubscribe = useAuthStore.getState().initializeAuth();
    return unsubscribe;
  }, []);

  // Read suggested email from URL params or SaaS SDK
  const [sdkEmail, setSdkEmail] = useState<string | null>(null);
  const { urlEmail, platformLabel, appCodeLower } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('suggestLoginMail') || params.get('userEmail') || null;
    // appCode may be path-based (/twilioformonday/compose) with no ?appCode=
    // param — Monday embeds the app that way. Without the path fallback the
    // SDK-email block below never ran, so the login screen showed no suggested
    // email. (Search param wins to preserve existing ?appCode= behavior.)
    const appCode = (params.get('appCode') || getAppCodeFromURL() || '').toLowerCase();
    const parsed = parseAppCode(appCode);
    const label = parsed ? getSaaSPrettyName(parsed.saas) : 'CRM';
    return { urlEmail: email, platformLabel: label, appCodeLower: appCode };
  }, []);

  // Resolve email from SaaS SDK when URL params don't have it
  useEffect(() => {
    if (urlEmail || user) return; // Already have email or already authenticated
    let cancelled = false;

    const resolveEmail = async () => {
      try {
        // Zoho CRM / Zoho Bigin / Zoho Recruit — SDK loaded eagerly by sdk-init.ts
        if (appCodeLower.includes('forzohocrm') || appCodeLower.includes('forzohobigin') || appCodeLower.includes('forzohorecruit')) {
          // Wait for SDK script to load
          const waitForZoho = () => new Promise<any>((resolve) => {
            if ((window as any).ZOHO?.embeddedApp) { resolve((window as any).ZOHO); return; }
            const interval = setInterval(() => {
              if ((window as any).ZOHO?.embeddedApp) { clearInterval(interval); resolve((window as any).ZOHO); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 8000);
          });
          const ZOHO = await waitForZoho();
          if (cancelled || !ZOHO?.embeddedApp) return;

          // Register PageLoad handler and init — store context globally for CRM module reuse
          ZOHO.embeddedApp.on('PageLoad', async (widgetContext: any) => {
            (window as any).__zoho_widget_context = widgetContext;
            try {
              const config = ZOHO.CRM?.CONFIG || ZOHO.BIGIN?.CONFIG;
              if (config) {
                const res = await config.getCurrentUser();
                if (!cancelled && res?.users?.[0]?.email) setSdkEmail(res.users[0].email);
              }
            } catch { /* non-critical */ }
          });
          if (!(window as any).__zoho_sdk_inited) {
            (window as any).__zoho_sdk_inited = true;
            ZOHO.embeddedApp.init().catch(() => {});
          }
          return;
        }

        // Zoho Desk — SDK loaded eagerly by sdk-init.ts
        if (appCodeLower.includes('forzohodesk')) {
          const waitForDesk = () => new Promise<any>((resolve) => {
            if ((window as any).ZOHODESK?.get) { resolve((window as any).ZOHODESK); return; }
            const interval = setInterval(() => {
              if ((window as any).ZOHODESK?.get) { clearInterval(interval); resolve((window as any).ZOHODESK); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 5000);
          });
          const ZOHODESK = await waitForDesk();
          if (cancelled) return;
          if (ZOHODESK?.get) {
            const res = await ZOHODESK.get('user');
            const email = res?.user?.email;
            if (!cancelled && email) setSdkEmail(email);
          }
          return;
        }

        // Freshdesk / Freshworks CRM — SDK loaded eagerly by sdk-init.ts
        if (appCodeLower.includes('forfreshdesk') || appCodeLower.includes('forfreshworkscrm')) {
          const waitForFresh = () => new Promise<any>((resolve) => {
            if ((window as any).app?.initialized) { resolve((window as any).app); return; }
            const interval = setInterval(() => {
              if ((window as any).app?.initialized) { clearInterval(interval); resolve((window as any).app); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 5000);
          });
          const freshApp = await waitForFresh();
          if (cancelled || !freshApp) return;
          const client = await freshApp.initialized();
          const res = await client.data.get('loggedInUser');
          const email = appCodeLower.includes('forfreshdesk')
            ? res?.loggedInUser?.contact?.email
            : res?.loggedInUser?.email;
          if (!cancelled && email) setSdkEmail(email);
          return;
        }

        // Monday.com — use Monday SDK to query current user email
        if (appCodeLower.includes('formonday')) {
          const waitForMonday = () => new Promise<any>((resolve) => {
            if ((window as any).mondaySdk) { resolve((window as any).mondaySdk); return; }
            const interval = setInterval(() => {
              if ((window as any).mondaySdk) { clearInterval(interval); resolve((window as any).mondaySdk); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 8000);
          });
          const sdk = await waitForMonday();
          if (cancelled || !sdk) return;
          try {
            const monday = sdk();
            const res = await monday.api('query { me { email } }');
            const email = res?.data?.me?.email;
            if (!cancelled && email) setSdkEmail(email);
          } catch { /* non-critical */ }
          return;
        }

        // Zoho Books — SDK loaded eagerly by sdk-init.ts (ZFAPPS)
        if (appCodeLower.includes('forzohobooks')) {
          const waitForZF = () => new Promise<any>((resolve) => {
            if ((window as any).ZFAPPS?.get) { resolve((window as any).ZFAPPS); return; }
            const interval = setInterval(() => {
              if ((window as any).ZFAPPS?.get) { clearInterval(interval); resolve((window as any).ZFAPPS); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 8000);
          });
          const ZFAPPS = await waitForZF();
          if (cancelled || !ZFAPPS?.get) return;
          try {
            const res = await ZFAPPS.get('user');
            if (!cancelled && res?.user?.email) setSdkEmail(res.user.email);
          } catch { /* non-critical */ }
          return;
        }

        // Bitrix24 — SDK loaded eagerly by sdk-init.ts (BX24)
        if (appCodeLower.includes('forbitrix24')) {
          const waitForBX = () => new Promise<any>((resolve) => {
            if ((window as any).BX24?.callMethod) { resolve((window as any).BX24); return; }
            const interval = setInterval(() => {
              if ((window as any).BX24?.callMethod) { clearInterval(interval); resolve((window as any).BX24); }
            }, 200);
            setTimeout(() => { clearInterval(interval); resolve(null); }, 8000);
          });
          const BX24 = await waitForBX();
          if (cancelled || !BX24?.callMethod) return;
          try {
            await BX24.callMethod('user.current', {}, (response: any) => {
              const email = response?.data?.()?.EMAIL;
              if (!cancelled && email) setSdkEmail(email);
            });
          } catch { /* non-critical */ }
          return;
        }

        // Zendesk Sell
        if (appCodeLower.includes('forzendesksell')) {
          const client = (window as any).ZAFClient?.init?.();
          if (client?._context?.currentUser?.email) {
            setSdkEmail(client._context.currentUser.email);
          }
          return;
        }
      } catch (err) {
        console.warn('[AuthGuard] Failed to resolve SDK email:', err);
      }
    };

    resolveEmail();
    return () => { cancelled = true; };
  }, [urlEmail, user, appCodeLower]);

  // Store fallback last: `suggestedLoginEmail` is resolved by the SaaS module
  // during init and survives signOut(), so it's the only source left after the
  // account-mismatch banner signs out — that path unmounts AppLayout and lands
  // here, and on hosts without ?suggestLoginMail or a matching SDK branch the
  // field would otherwise render empty.
  const suggestedEmail = urlEmail || sdkEmail || storeSuggestedEmail;

  // Show skeleton layout until auth state is resolved
  if (!initialized || loading) {
    return <AppSkeleton />;
  }

  if (!user) {
    // Inside iframe: show inline login form (no redirect to separate login page)
    if (isInIframe) {
      return (
        <InlineLoginForm
          suggestedEmail={suggestedEmail || undefined}
          platformLabel={platformLabel}
        />
      );
    }
    // Outside iframe: redirect to login page as usual. Carry the suggested
    // account across so the form pre-fills — `nextpage` alone buries it inside
    // an encoded path where LoginForm can't see it.
    const currentPath = location.pathname + location.search;
    const loginUrl = handoffLoginUrl(suggestedEmail, currentPath);
    return <Navigate to={loginUrl} state={{ from: location }} replace />;
  }

  // Block unverified email users
  if (needsEmailVerification) {
    return <EmailVerificationPrompt />;
  }

  if (requireAdmin && !user.isAdmin) {
    return <Navigate to="/send-sms" replace />;
  }

  return <>{children}</>;
}
